import clsx from "clsx";

import { useNavigate } from "react-router-dom";

import { BalanceGroup, UserPermissions } from "@/entities/user";
import { MaxWidthWrapper } from "@/shared/ui/MaxWidthWrapper";

import { getNavRoutes } from "../model/nav-routes";
import { LogoutModal } from "@/widgets/LogoutModal";
import { ProfileModal } from "@/widgets/ProfileModal";
import { SendNotificationCard } from "@/widgets/SendNotificationCard/SendNotificationCard";
import { BroadcastToLeadsModal } from "@/widgets/BroadcastToLeadsModal";
import { SwitchThemeButton } from "@/features/SwitchThemeButton";
import { SendNotificationBanner } from "@/widgets/SendNotificationBanner";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { MasterTeamleadModal } from "@/widgets/MasterTeamleadModal";
import { Roles } from "@/entities/user";
import { PhonesExportModal } from "@/widgets/PhoneExportModal";
import { TelegramAssignButton } from "@/features/TelegramAssignButton";
import { useSessionStore } from "@/entities/session";
import { ConvertBalancesCard } from "@/widgets/ConvertBalancesCard";
import { CollaborateModal } from "@/widgets/CollaborateModal";
import { useIsClanLeader, useIsClanMember } from "@/entities/clans";
import { CreateClanProfileModal } from "@/widgets/CreateClanProfileModal";
import { SeeClanContactsModal } from "@/widgets/SeeClanContactsModal";

interface SideBarProps {
  isOpen: boolean;
}

export const SideBar = ({ isOpen }: SideBarProps) => {
  const handleNavigate = useNavigate();
  const { user } = useSessionStore();

  const isLeader = useIsClanLeader();
  const isMember = useIsClanMember();
  const role = user?.role!;
  const permissions = user?.permissions;

  return (
    <div
      className={clsx(
        "fixed flex flex-col max-lg:hidden",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "left-0 top-0 h-[100dvh] max-h-dvh w-[270px] bg-bgColor",
        "transition-transform duration-300",
      )}
    >
      <MaxWidthWrapper className="flex items-center justify-between p-4">
        <ProfileModal user={user} />
      </MaxWidthWrapper>

      <hr className="border-[#39393940] dark:border-[#EDEDED26]" />

      <MaxWidthWrapper className="flex flex-1 flex-col py-4">
        <div className="flex flex-1 flex-col gap-1">
          {role !== Roles.Worker && <SwitchThemeButton />}
          {[Roles.Worker, Roles.Head].includes(role) && (
            <div className="space-y-2">
              <BalanceGroup />
              {(user?.balances?.find((b) => b.asset === "FT_COIN")?.balance ||
                0) > 0 && <ConvertBalancesCard />}

              <CollaborateModal />
            </div>
          )}

          <TelegramAssignButton />
          {isLeader && <CreateClanProfileModal />}
          {isMember && <SeeClanContactsModal />}

          <div className="flex flex-1 flex-col overflow-hidden pt-4">
            <div className="flex flex-1 flex-col overflow-hidden">
              <ScrollArea className="h-full" type="auto">
                <ul className="absolute inset-0 flex flex-col gap-4">
                  {getNavRoutes(
                    role || "",
                    user?.permissions ?? [],
                    isLeader ?? false,
                  ).map(({ label, link, icon }) => {
                    if (
                      link === "/bots" &&
                      role !== Roles.Head &&
                      !permissions?.includes(UserPermissions.CONSTRUCTOR_BOT)
                    )
                      return;

                    return (
                      <li
                        key={label}
                        className="group flex w-full items-center gap-2 pb-2 hover:cursor-pointer"
                        onClick={() =>
                          label.toLowerCase() === "главная"
                            ? handleNavigate("/")
                            : handleNavigate(link)
                        }
                      >
                        {icon}
                        <span className="bg-gradient-to-r from-gray-500 to-gray-500 bg-[length:0%_2px] bg-left-bottom bg-no-repeat pb-1 text-textColor transition-all duration-500 ease-out group-hover:bg-[length:100%_2px]">
                          {label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>
          </div>
        </div>

        <div className="flex max-h-[50%] w-full flex-col gap-4">
          {role === Roles.Head && <SendNotificationBanner />}
          {role === Roles.Head && <SendNotificationCard />}
          {role === Roles.Head && <PhonesExportModal />}
          {role === Roles.Head && <BroadcastToLeadsModal />}
          {role === Roles.Worker && <SwitchThemeButton />}
          {localStorage.getItem("master-teamlead-store") ||
          role === Roles.MasterTeamLead ? (
            <MasterTeamleadModal />
          ) : (
            <div className="flex w-full items-center justify-between">
              <LogoutModal />
              <span className="text-textGrayColor">⌘B</span>
            </div>
          )}
        </div>
      </MaxWidthWrapper>
    </div>
  );
};
